import { FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, auth } from "../api";
import { Button, Card, Empty, Field, Input, Row, Rows, Section } from "../components/ui";

interface Tenant {
  id: number;
  slug: string;
  name: string;
  timezone: string;
  status: string;
  subscription_state: string;
}

export default function Customers() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const tenants = useQuery<Tenant[]>({
    queryKey: ["tenants"],
    queryFn: () => api.get<Tenant[]>("/api/admin/tenants"),
  });

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [timezone, setTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
  );

  const create = useMutation({
    mutationFn: () => api.post<Tenant>("/api/admin/tenants", { name, slug, timezone }),
    onSuccess: (t) => {
      qc.invalidateQueries({ queryKey: ["tenants"] });
      setName("");
      setSlug("");
      nav(`/customers/${t.id}`);
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate();
  };

  const logout = () => {
    auth.clear();
    nav("/login");
  };

  return (
    <div className="min-h-screen bg-canvas p-6">
      <div className="mx-auto max-w-4xl space-y-6">
        <header className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-card bg-primary font-bold text-white">
              R
            </div>
            <div>
              <div className="font-semibold">report-bot</div>
              <div className="text-xs text-muted">Developer console</div>
            </div>
          </div>
          <Button variant="ghost" onClick={logout}>
            Sign out
          </Button>
        </header>

        <Section title="Customers" hint="Each one is a DataTruck account plus the chats it reports into.">
          {tenants.isLoading && <Empty>Loading…</Empty>}
          {tenants.data?.length === 0 && <Empty>No customers yet.</Empty>}
          <Rows>
            {tenants.data?.map((t) => (
              <Row key={t.id}>
                <Link to={`/customers/${t.id}`} className="font-medium text-primary">
                  {t.name}
                </Link>
                <span className="text-muted">
                  {t.slug} · {t.timezone}
                </span>
              </Row>
            ))}
          </Rows>
        </Section>

        <Card>
          <div className="mb-4 font-medium">Add a customer</div>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid gap-4 md:grid-cols-3">
              <Field label="Name">
                <Input value={name} onChange={(e) => setName(e.target.value)} required />
              </Field>
              <Field label="Slug">
                <Input
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  // Escaped: an unescaped `-` here makes the pattern invalid
                  // under the `v` flag, and the browser then skips validating
                  // the field at all.
                  pattern="[a-z0-9\-]+"
                  placeholder="acme"
                  required
                />
              </Field>
              <Field label="Timezone">
                <Input value={timezone} onChange={(e) => setTimezone(e.target.value)} required />
              </Field>
            </div>
            {create.error && (
              <div className="text-sm text-danger">{(create.error as Error).message}</div>
            )}
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? "Creating…" : "Create customer"}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
